// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $FullChampionshipResultTable,
  $FullChampionshipResultTableEntry,
  $FullChampionshipResultTableRace,
  $Model,
  $ModelUpsertParams,
  $RegattaResource,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  SectionHeader,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  isDiff,
  numberValue,
} from '@vroskus/library-helpers/dist/browser';
import {
  getChampionshipEntrySubTitle,
  getChampionshipEntryTitle,
  shortenText,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
} from '../../../../helpers';

// Components
import EmptyTable from '../../../common/EmptyTable';
import ChampionshipResultTableFields from '../../../fields/ChampionshipResultTableFields';
import ChampionshipResultTableEntryFields from '../../../fields/ChampionshipResultTableEntryFields';
import ChampionshipResultTableRaceFields from '../../../fields/ChampionshipResultTableRaceFields';
import ChampionshipResultTableRaceResultFields from '../../../fields/ChampionshipResultTableRaceResultFields';

// Services
import Services from '../../../../services';

// Types
type $Props = $Translate & {
  id: string;
  item: $Model<'Championship'>;
};

type $State = {
  data: Array<$FullChampionshipResultTable>;
  resourceData: Array<$RegattaResource>;
};

type $GetRegattaEntryUniqueKey = (arg0: {
  regattaEntry: $Model<'RegattaEntry'>;
}) => string;

const getRegattaEntryUniqueKey: $GetRegattaEntryUniqueKey = ({
  regattaEntry,
}) => {
  const {
    Name,
    RegistrationNo,
  } = regattaEntry;

  return `${RegistrationNo}${Name || ''}`.toUpperCase();
};

class ChampionshipResultTablesSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      resourceData: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        const models = [
          'ChampionshipResultTable',
          'ChampionshipResultTableEntry',
          'ChampionshipResultTableRace',
        ];

        if (item.id === data.ChampionshipId && _.includes(
          models,
          data.modelName,
        )) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        const data = await Services.Api.getManyChampionshipResultTable({
          ChampionshipId,
        });

        await this.setStateAsync({
          data,
        });
      },
    });
  }

  async getResourceData(): Promise<void> {
    const {
      item,
    } = this.props;

    const ChampionshipId = item.id;

    await defaultGetData({
      action: async () => {
        const resourceData = await Services.Api.getResourceChampionshipRegatta({
          ChampionshipId,
        });

        await this.setStateAsync({
          resourceData,
        });
      },
    });
  }

  getUnusedRegattaEntries(
    championshipResultTable: $FullChampionshipResultTable,
  ): Array<$Model<'RegattaEntry'>> {
    const {
      resourceData,
    } = this.state;

    const unusedRegattaEntriesObject: Record<string, $Model<'RegattaEntry'>> = {
    };

    resourceData.forEach((regatta: $RegattaResource) => {
      regatta.RegattaEntries.forEach((regattaEntry: $Model<'RegattaEntry'>) => {
        const regattaEntryKey = getRegattaEntryUniqueKey({
          regattaEntry,
        });

        let regattaEntryAlready = false;

        _.forEach(
          championshipResultTable.ChampionshipResultTableEntries,
          (championshipResultTableEntry: $FullChampionshipResultTableEntry) => {
            const championshipResultTableEntryKey = getRegattaEntryUniqueKey({
              regattaEntry: championshipResultTableEntry.RegattaEntry,
            });

            if (regattaEntryAlready === false
              && regattaEntryKey === championshipResultTableEntryKey
            ) {
              regattaEntryAlready = true;
            }
          },
        );

        if (regattaEntryAlready === false) {
          unusedRegattaEntriesObject[regattaEntryKey] = regattaEntry;
        }
      });
    });

    const unusedRegattaEntries: Array<$Model<'RegattaEntry'>> = _.sortBy(
      _.values(unusedRegattaEntriesObject),
      'RegistrationNo',
    );

    return unusedRegattaEntries;
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const ChampionshipId = item.id;

    const newItem: $ModelUpsertParams<'ChampionshipResultTable'> = {
      ChampionshipId,
      Discards: 0,
      InvertPoints: false,
      Name: '',
      Public: false,
    };

    return (
      <div className={'bg-white rounded'}>
        <SectionHeader
          bottomBorder={data.length === 0}
          iconClass={'icon-trophy'}
          rightButtons={(
            <FormDialog
              fields={(
                <ChampionshipResultTableFields item={newItem} />
              )}
              id={'create-championship-result-table-dialog'}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'ChampionshipResultTable'>>({
                    ...newItem,
                    ...values,
                    Discards: numberValue(values.Discards),
                  });

                  await Services.Api.upsertChampionshipResultTable(params);
                },
              })}
              subTitle={t('New championship result table')}
              title={t('Championship result table')}
            >
              <Button
                color={'info'}
                id={'create-championship-result-table-dialog'}
                size={'sm'}
                title={t('Add new championship result table')}
              >
                {t('Add new')}
              </Button>
            </FormDialog>
          )}
          title={t('Championship results')}
        />
      </div>
    );
  }

  renderChampionshipResultTableHeader(
    championshipResultTable: $FullChampionshipResultTable,
  ) {
    const {
      t,
    } = this.props;
    const {
      resourceData,
    } = this.state;

    const newChampionshipResultTableEntry = {
      ChampionshipResultTableId: championshipResultTable.id,
      NameOverride: null,
      Place: null,
      Points: 0,
      RegattaEntryId: '',
    };

    const newChampionshipResultTableRace = {
      ChampionshipResultTableId: championshipResultTable.id,
      Coef: 1,
      Dominant: false,
      Name: '',
      RegattaId: '',
      Slug: '',
      Undiscard: false,
    };

    return (
      <tr key={'header'}>
        <th
          className={'fix'}
          key={'addEntry'}
        >
          <div>
            {shortenText(
              championshipResultTable.Name,
              23,
            )}
          </div>
          <div className={'d-flex justify-content-end'}>
            <FormDialog
              fields={(
                <ChampionshipResultTableEntryFields
                  item={newChampionshipResultTableEntry}
                  resourceData={this.getUnusedRegattaEntries(championshipResultTable)}
                />
              )}
              id={`create-championship-result-table-entry-dialog-${championshipResultTable.id}`}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'ChampionshipResultTableEntry'>>({
                    ...newChampionshipResultTableEntry,
                    ...values,
                  });

                  await Services.Api.upsertChampionshipResultTableEntry(params);
                },
              })}
              subTitle={t('New championship result table entry')}
              title={t('Championship result table entry')}
            >
              <Button
                color={'info'}
                id={`create-championship-result-table-entry-button-${championshipResultTable.id}`}
                size={'sm'}
              >
                {t('Add entry')}
              </Button>
            </FormDialog>
          </div>
        </th>
        {championshipResultTable.ChampionshipResultTableRaces.map((
          championshipResultTableRace: $FullChampionshipResultTableRace,
        ) => (
          <th key={championshipResultTableRace.id}>
            <div className={'text-center'}>
              {championshipResultTableRace.Slug}
            </div>
            <div>
              <FormDialog
                fields={(
                  <ChampionshipResultTableRaceFields
                    item={championshipResultTableRace}
                    resourceData={resourceData}
                  />
                )}
                id={`update-championship-result-table-race-dialog-${championshipResultTable.id}`}
                labelCloseButton={t('Close')}
                labelSaveButton={t('Save')}
                onAction={(values) => defaultUpsertItem({
                  action: async () => {
                    const params = cleanFormValues<$Model<'ChampionshipResultTableRace'>>({
                      ...championshipResultTableRace,
                      ...values,
                    });

                    await Services.Api.upsertChampionshipResultTableRace(params);
                  },
                })}
                subTitle={championshipResultTableRace.Name}
                title={t('Championship result table race')}
              >
                <Button
                  className={'mr-1'}
                  color={'warning'}
                  id={`update-championship-result-table-race-button-${championshipResultTable.id}`}
                  outline
                  size={'sm'}
                  title={t('Edit race')}
                >
                  <i className={'icon-note'} />
                </Button>
              </FormDialog>
              <DeleteDialog
                content={t('Do you really want to delete?')}
                id={`delete-championship-result-table-race-dialog-${championshipResultTable.id}`}
                labelCloseButton={t('Close')}
                labelDeleteButton={t('Delete')}
                onAction={() => defaultDeleteItem({
                  action: async () => Services.Api.deleteChampionshipResultTableRace(
                    championshipResultTableRace,
                  ),
                })}
                subTitle={championshipResultTableRace.Name}
                title={t('Championship result table race')}
              >
                <Button
                  color={'danger'}
                  id={`delete-championship-result-table-race-button-${championshipResultTable.id}`}
                  outline
                  size={'sm'}
                  title={t('Delete race')}
                >
                  <i className={'icon-trash'} />
                </Button>
              </DeleteDialog>
            </div>
          </th>
        ))}
        <th
          className={'last'}
          key={'addRace'}
          style={{
            height: 68,
          }}
        >
          <FormDialog
            fields={(
              <ChampionshipResultTableRaceFields
                item={newChampionshipResultTableRace}
                resourceData={resourceData}
              />
            )}
            id={`create-championship-result-table-race-dialog-${championshipResultTable.id}`}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'ChampionshipResultTableRace'>>({
                  ...newChampionshipResultTableRace,
                  ...values,
                });

                await Services.Api.upsertChampionshipResultTableRace(params);
              },
            })}
            subTitle={t('New championship result table race')}
            title={t('Championship result table race')}
          >
            <Button
              color={'info'}
              id={`create-championship-result-table-race-button-${championshipResultTable.id}`}
              size={'sm'}
              style={{
                width: 100,
              }}
            >
              {t('Add regatta')}
            </Button>
          </FormDialog>
        </th>
      </tr>
    );
  }

  renderChampionshipResultTableContent(
    championshipResultTable: $FullChampionshipResultTable,
  ) {
    const {
      t,
    } = this.props;

    return (
      <>
        {championshipResultTable.ChampionshipResultTableEntries.length === 0 && (
          <tr>
            <td className={'fix'} />
            <td
              className={'text-center text-muted p-3'}
              colSpan={100}
            >
              {t('Add first regatta entry')}
            </td>
          </tr>
        )}
        {championshipResultTable.ChampionshipResultTableEntries.map((
          championshipResultTableEntry: $FullChampionshipResultTableEntry,
        ) => (
          <tr key={championshipResultTableEntry.id}>
            <td className={'py-1 fix'}>
              <Row>
                <Col
                  className={'d-flex align-items-center'}
                  xs={8}
                >
                  <div className={'lh-1'}>
                    <div>
                      {shortenText(
                        getChampionshipEntryTitle({
                          championshipResultTableEntry,
                        }),
                        16,
                      )}
                    </div>
                    <small>
                      {shortenText(
                        getChampionshipEntrySubTitle({
                          championshipResultTableEntry,
                        }),
                        20,
                      )}
                    </small>
                  </div>
                </Col>
                <Col
                  className={'d-flex justify-content-end'}
                  xs={4}
                >
                  <FormDialog
                    fields={(
                      <ChampionshipResultTableEntryFields
                        item={championshipResultTableEntry}
                        resourceData={[championshipResultTableEntry.RegattaEntry]}
                      />
                    )}
                    id={`update-championship-result-table-entry-dialog-${championshipResultTable.id}`}
                    labelCloseButton={t('Close')}
                    labelSaveButton={t('Save')}
                    onAction={(values) => defaultUpsertItem({
                      action: async () => {
                        const params = cleanFormValues<$Model<'ChampionshipResultTableEntry'>>({
                          ...championshipResultTableEntry,
                          ...values,
                        });

                        await Services.Api.upsertChampionshipResultTableEntry(params);
                      },
                    })}
                    subTitle={championshipResultTableEntry.RegattaEntry.Name || ''}
                    title={t('Championship result table entry')}
                  >
                    <Button
                      className={'mr-1'}
                      color={'warning'}
                      id={`update-championship-result-table-entry-button-${championshipResultTable.id}`}
                      outline
                      size={'sm'}
                      title={t('Edit championship result table entry')}
                    >
                      <i className={'icon-note'} />
                    </Button>
                  </FormDialog>
                  <DeleteDialog
                    content={t('Do you really want to delete?')}
                    id={`dialog-championship-result-table-entry-dialog-${championshipResultTable.id}`}
                    labelCloseButton={t('Close')}
                    labelDeleteButton={t('Delete')}
                    onAction={() => defaultDeleteItem({
                      action: async () => Services.Api.deleteChampionshipResultTableEntry(
                        championshipResultTableEntry,
                      ),
                    })}
                    subTitle={championshipResultTableEntry.RegattaEntry.Name || ''}
                    title={t('Championship result table entry')}
                  >
                    <Button
                      color={'danger'}
                      id={`dialog-championship-result-table-entry-button-${championshipResultTable.id}`}
                      outline
                      size={'sm'}
                      title={t('Delete entry')}
                    >
                      <i className={'icon-trash'} />
                    </Button>
                  </DeleteDialog>
                </Col>
              </Row>
            </td>
            {championshipResultTable.ChampionshipResultTableRaces.map((
              championshipResultTableRace: $FullChampionshipResultTableRace,
            ) => (
              <>
                {championshipResultTableRace.ChampionshipResultTableRaceResults.filter((
                  result: $Model<'ChampionshipResultTableRaceResult'>,
                ) => (
                  result.ChampionshipResultTableEntryId === championshipResultTableEntry.id
                )).map((
                  result: $Model<'ChampionshipResultTableRaceResult'>,
                ) => (
                  <td
                    className={`py-1 ${result.Discarded ? 'bg-gray-light' : ''}`}
                    key={championshipResultTableRace.id}
                  >
                    <ChampionshipResultTableRaceResultFields
                      item={result}
                      onChange={(values) => defaultUpsertItem({
                        action: async () => {
                          const params = cleanFormValues<$Model<'ChampionshipResultTableRaceResult'>>({
                            ...result,
                            ...values,
                            InputDate: new Date(),
                          });

                          await Services.Api.updateChampionshipResultTableRaceResult(params);
                        },
                        quiet: true,
                      })}
                    />
                  </td>
                ))}
              </>
            ))}
            <td className={'py-1 last'}>
              <div
                style={{
                  width: 105,
                }}
              >
                <Row className={'no-gutters'}>
                  <Col
                    className={'text-center py-2 text-muted'}
                    xs={6}
                  >
                    {championshipResultTableEntry.Points}
                  </Col>
                  <Col
                    className={'text-center py-2 text-bold'}
                    xs={6}
                  >
                    {championshipResultTableEntry.Place}
                  </Col>
                </Row>
              </div>
            </td>
          </tr>
        ))}
      </>
    );
  }

  renderChampionshipResultTableFooter(
    championshipResultTable: $FullChampionshipResultTable,
  ) {
    const {
      t,
    } = this.props;

    return (
      <CardBody className={'text-right border-thin-top py-2'}>
        <FormDialog
          fields={(
            <ChampionshipResultTableFields item={championshipResultTable} />
          )}
          id={`update-championship-result-table-dialog-${championshipResultTable.id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'ChampionshipResultTable'>>({
                ...championshipResultTable,
                ...values,
                Discards: numberValue(values.Discards),
              });

              await Services.Api.upsertChampionshipResultTable(params);
            },
          })}
          subTitle={championshipResultTable.Name}
          title={t('Championship result table')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-championship-result-table-button-${championshipResultTable.id}`}
            outline
            size={'sm'}
          >
            {t('Edit table')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-championship-result-table-dialog-${championshipResultTable.id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteChampionshipResultTable(championshipResultTable),
          })}
          subTitle={championshipResultTable.Name}
          title={t('Championship result table')}
        >
          <Button
            color={'danger'}
            id={`delete-championship-result-table-button-${championshipResultTable.id}`}
            outline
            size={'sm'}
          >
            {t('Delete table')}
          </Button>
        </DeleteDialog>
      </CardBody>
    );
  }

  renderChampionshipResultTable(
    championshipResultTable: $FullChampionshipResultTable,
  ) {
    return (
      <Card
        className={'ChampionshipResultTable'}
        key={championshipResultTable.id}
      >
        <div className={'ChampionshipResultTableBody'}>
          <Table>
            <thead>
              {this.renderChampionshipResultTableHeader(championshipResultTable)}
            </thead>
            <tbody>
              {this.renderChampionshipResultTableContent(championshipResultTable)}
            </tbody>
          </Table>
        </div>
        {this.renderChampionshipResultTableFooter(championshipResultTable)}
      </Card>
    );
  }

  renderContent() {
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <>
        {data.map((
          championshipResultTable: $FullChampionshipResultTable,
        ) => this.renderChampionshipResultTable(championshipResultTable))}
      </>
    );
  }

  render() {
    const {
      id,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <Card
        id={id}
        style={{
          backgroundColor: data.length === 0 ? '#fff' : 'rgba(200,200,200,0.2)',
          borderWidth: 0,
        }}
      >
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default ChampionshipResultTablesSectionView;
